import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import {
  ButtonWithIcon,
  Content,
  PageHeader,
  Spinner,
  useTo,
  Tabs,
  Tab,
  TabList,
  TabContent,
  useMediaModal,
} from 'scorer-ui-kit';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getDeviceStatus } from '../lib/api_config';
import { getTimeSince } from '../util';
import { useTranslation } from 'react-i18next';
import SourceFilesTab from './SourceFilesTab';
import AnalysisJobsTab from './AnalysisJobsTab';
import { useDevices } from '../hooks/useDevices';
import NoImage from '../images/no-image.png';
import { format } from 'date-fns';

const PageContent = styled(Content)`
  margin-bottom: 20px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 50px;
`;

const PageHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: auto;
  height: 110px;
`;

const Snapshot = styled.img`
  width: auto;
  height: 110px;
  border-radius: 3px;
  cursor: pointer;
`;

const ActiveLabel = styled.label`
  font-family: Lato;
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  text-align: left;
  color: #a5a5a5;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  border-radius: 3px;
  background-color: #efefef;
`;

const SpinnerContainer = styled.div`
  height: 110px;
  text-align: center;
  background: hsla(0, 0%, 82.7%, 1.000);
  padding: 40px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;
`;

interface Params {
  lid: string
}

export interface IDeviceDetailsParam{
  selectedTab: string | null;
  status?: string | null;
  sortByAJ? : string | null;
  startDateAJ: string | null;
  endDateAJ: string | null;
  startDateSF: string | null,
  endDateSF: string| null
}

function CameraDetails() {
  const { lid }: Params = useParams();
  const [deviceName, setDeviceName] = useState<string>('');
  const [statusData, setStatusData] = useState({lastSnapshot: {signedUrl:'', time:''}});
  const {state: {devices}, actions:{fetchDevices}} = useDevices();
  const { t } = useTranslation(['CameraDetails', 'Common']);
  const [loading, setLoading] = useState<boolean>(true);
  const to = useTo();
  const [selectedTab, setSelectedTab] = useState<string>('sourceFiles');
  const history = useHistory();
  const { createMediaModal, isMediaUrlValid } = useMediaModal();
  const [cameraType, setCameraType] = useState<string>('');
  const params = useLocation().search;
  const [ historyParams ] = useState<string>(params);
  const [status, setStatus] = useState<string | null>('');
  const [ajSort, setAjSort] = useState({ value: 'recordingTime', ascending: false, text: t('Recording Time') });
  const [sfDates, setSfDates] = useState<any>(null);
  const [ajDates, setAjDates] = useState<any>(null);

  
  const setParams = useCallback(() => {
    const params = `?selectedTab=${selectedTab}` + 
    `${status === null || status === '' ? '' : '&status=' + status}` + 
    `${sfDates !== null ? '&startDateSF=' + sfDates.startDateSF : ''}` +
    `${sfDates !== null ? '&endDateSF=' + sfDates.endDateSF : ''}` +
    `${ajDates !== null ? '&startDateAJ=' + ajDates.startDateAJ : ''}` +
    `${ajDates !== null ? '&endDateAJ=' + ajDates.endDateAJ : ''}` +
    `${!ajSort.ascending && ajSort.value === 'recordingTime' ? '' : '&sortByAJ=' + JSON.stringify(ajSort)}`;
    if (params === '?') {
      return '';
    }
    return params;
  }, [status, selectedTab, ajSort, ajDates, sfDates]);

  useEffect(() => {
    history.replace(window.location.pathname + setParams());
  }, [history, setParams]);

  const getStatus = useCallback((status: string) => {
    setStatus(status);
  },[]);

  const getAPSorting = useCallback((sort: any) => {
    setAjSort(sort);
  },[]);

  const getSFDates = useCallback((dates: any) => {
    setSfDates(dates);
  },[]);

  const getAJDates = useCallback((dates: any) => {
    setAjDates(dates);
  },[]);

  const fetchHistoryParams = useCallback(() => {
    const urlString = new URLSearchParams(historyParams);
    const newParams: IDeviceDetailsParam = {
      selectedTab: null,
      status: '',
      sortByAJ: '',
      startDateAJ: '',
      endDateAJ: '',
      startDateSF: '',
      endDateSF: ''
    };
    newParams.selectedTab = urlString.get('selectedTab');
    newParams.startDateSF = urlString.get('startDateSF');
    newParams.endDateSF = urlString.get('endDateSF');
    newParams.status = urlString.get('status');
    newParams.sortByAJ = urlString.get('sortByAJ');
    newParams.startDateAJ = urlString.get('startDateAJ');
    newParams.endDateAJ = urlString.get('endDateAJ');

    if(newParams.selectedTab === null){
      setSelectedTab('sourceFiles');
    }
    if(newParams.selectedTab==='sourceFiles'){
      setSelectedTab(newParams.selectedTab);
    }
    if(newParams.selectedTab==='analysisJobs'){
      setSelectedTab(newParams.selectedTab);
    }
    if(newParams.startDateSF !== null && newParams.endDateSF !== null) {
      setSfDates({startDateSF: format(new Date(newParams.startDateSF), 'yyyy-MM-dd HH:mm:ss'), endDateSF: format(new Date(newParams.endDateSF),'yyyy-MM-dd HH:mm:ss')});
    }
    if(newParams.status !== '') {
      setStatus(newParams.status);
    }
    if(newParams.sortByAJ) {
      setAjSort(prev => (newParams.sortByAJ ? JSON.parse(newParams.sortByAJ) : prev));
    }
    if(newParams.startDateAJ !== null && newParams.endDateAJ !== null) {
      setAjDates({startDateAJ: format(new Date(newParams.startDateAJ), 'yyyy-MM-dd HH:mm:ss'), endDateAJ: format(new Date(newParams.endDateAJ),'yyyy-MM-dd HH:mm:ss')});
    }

  }, [ historyParams, setSelectedTab]);

  useEffect(() => {
    fetchHistoryParams();
  }, [ fetchHistoryParams]);

  const handleTabChange = useCallback((tabName: string) => {
    setSelectedTab(tabName);
  }, []);

  const fetchDeviceStatus = useCallback(async () => {
    const dStatus = await getDeviceStatus(lid);
    setStatusData(dStatus);
    setLoading(false);
  }, [lid]);

  const handleModal = useCallback(async (src: string)=>{
    const isUrlValid = await isMediaUrlValid(src, 'img');
    if(isUrlValid) {
      createMediaModal({src, mediaType:'img', closeText:t('Common:close')});
    }
  },[createMediaModal, isMediaUrlValid,t]);

  useEffect(()=>{
    const matchedDevices = devices.filter(device => device.lid === lid);
    if(matchedDevices && matchedDevices.length > 0){
      setDeviceName(matchedDevices[0].name);
      setCameraType(matchedDevices[0].type);
    }
  },[devices, lid]);

  useEffect(()=>{
    fetchDevices();
  },[fetchDevices]);

  useEffect(() => {
    fetchDeviceStatus();
  }, [fetchDeviceStatus]);

  const onError = useCallback(() => {
    setStatusData({lastSnapshot:{ ...statusData.lastSnapshot, signedUrl: NoImage}});
  },[statusData.lastSnapshot]);


  return (
    <PageContent>
      <HeaderContainer>
        <PageHeaderContainer>
          <PageHeader title={deviceName} areaTitle={t('cameras')} areaHref='/' icon='Camera' updateDocTitle={false} />
          {statusData.lastSnapshot.time && <ActiveLabel>{t('CameraList:lastActivity')}: {getTimeSince (statusData.lastSnapshot.time)}</ActiveLabel>}
        </PageHeaderContainer>
        {
          (loading) ?
            <SpinnerContainer>
              <Spinner size='large' styling='primary' />
            </SpinnerContainer>
            :
            <Snapshot src={statusData.lastSnapshot.signedUrl} onError={onError} onClick={() => handleModal(statusData.lastSnapshot.signedUrl)} />
        }
      </HeaderContainer>

      <Tabs>
        <TabList defaultTabId={selectedTab}>
          <Tab tabFor='sourceFiles' onClick={() => handleTabChange('sourceFiles')}>{t('sourceFiles')}</Tab>
          <Tab tabFor='analysisJobs' onClick={() => handleTabChange('analysisJobs')}>{t('analysisJobs')}</Tab>
          {(cameraType && cameraType !== 'folder') &&
            <ButtonContainer>
              <ButtonWithIcon onClick={to(`/cameras/${lid}/analysis/create`)} design='primary' position='left' icon='Add' size='small'> {t('newAnalysis')}</ButtonWithIcon>
            </ButtonContainer>}   
        </TabList>
        <Divider />
        <TabContent tabId='sourceFiles'>
          <SourceFilesTab cameraType={cameraType} getSFDates={getSFDates} sfDates={sfDates} />
        </TabContent>
        <TabContent tabId='analysisJobs'>
          <AnalysisJobsTab getStatus={getStatus} statusParent={status} getAPSorting={getAPSorting} getAJDates={getAJDates} ajDates={ajDates} />
        </TabContent>
      </Tabs>
    </PageContent>
  );
}

export default CameraDetails;